import Navbar from "./Navabar";
import { FileText, Film, Code, Link as LinkIcon } from 'lucide-react';
import { Rocket, Coins, Compass, Lightbulb, Cpu, FlaskConical } from 'lucide-react';
import { MessageCircle, Users, BookOpen } from 'lucide-react';
import Footer from '../footer'

export default function AxionNestLanding({ AuthComponent }) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 rounded-full">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500"></div>
                <span className="text-sm font-semibold text-gray-700">
                  Research × Learning × AI × Industry
                </span>
              </div>
            </div>

            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
                Your integrated space for{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                  research, teaching, and industry collaboration
                </span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
                Build a living academic profile with publications, skills, code, lab work
                and media — then connect with industry partners, turn research into courses, 
                and accelerate innovation with AI.
              </p>

              <div className="pt-4">
                <div className="inline-block px-5 py-3 bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 rounded-lg">
                  <p className="text-gray-700 font-medium">
                    One place for your papers, code, courses, industry partnerships, and AI copilot.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Auth Component */}
          <div className="lg:sticky lg:top-8">
            {AuthComponent}
          </div>
        </div>
      </main>

      {/* Features Sections */}
      <div className="bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 space-y-20 lg:space-y-28">

          {/* Academic Identity Section */}
          <section id="publications" className="relative">
            <div className="absolute -left-8 top-0 w-16 h-16 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full opacity-50"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Academic Profile</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Academic identity in one link
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mb-12">
                Show who you are with a living profile: papers, code, projects,
                talks and media on{" "}
                <span className="font-semibold text-blue-600">your-name.axionnest</span>.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    tag: "Profile",
                    title: "All your work, together",
                    desc: "Publications, skills, projects and roles in one clean timeline.",
                    icon: <FileText className="w-5 h-5" />,
                    color: "bg-blue-100 text-blue-600"
                  },
                  {
                    tag: "Media",
                    title: "Figures & demos",
                    desc: "Images, videos and short clips that make your research visible.",
                    icon: <Film className="w-5 h-5" />,
                    color: "bg-purple-100 text-purple-600"
                  },
                  {
                    tag: "Code & data",
                    title: "From paper to repo",
                    desc: "Link repos, notebooks and datasets so others can actually run your work.",
                    icon: <Code className="w-5 h-5" />,
                    color: "bg-green-100 text-green-600"
                  },
                  {
                    tag: "Public page",
                    title: "Your own URL",
                    desc: "Share one link that connects your research, teaching and projects.",
                    icon: <LinkIcon className="w-5 h-5" />,
                    color: "bg-orange-100 text-orange-600"
                  }
                ].map((item, index) => (
                  <div key={index} className="group">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color}`}>
                          {item.icon}
                        </div>
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                          {item.tag}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Courses Section */}
          <section id="cours" className="relative">
            <div className="absolute -right-8 bottom-0 w-16 h-16 bg-gradient-to-r from-emerald-100 to-blue-100 rounded-full opacity-50"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Learning Platform</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Turn research into learning
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mb-12">
                Create courses, code sessions and simplified explainers directly
                from your work — free or paid.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    tag: "Creators",
                    title: "Build courses fast",
                    desc: "Start from PDFs or notes, then structure them into short modules and projects.",
                    icon: <Rocket className="w-5 h-5" />,
                    color: "bg-orange-100 text-orange-600"
                  },
                  {
                    tag: "Monetize",
                    title: "Advanced content, paid",
                    desc: "Offer deep-dive classes, live workshops or code labs for your niche.",
                    icon: <Coins className="w-5 h-5" />,
                    color: "bg-emerald-100 text-emerald-600"
                  },
                  {
                    tag: "Learners",
                    title: "Guided paths",
                    desc: "Follow AI-assisted tracks that match your level and goals.",
                    icon: <Compass className="w-5 h-5" />,
                    color: "bg-blue-100 text-blue-600"
                  }
                ].map((item, index) => (
                  <div key={index} className="group">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-emerald-200 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color}`}>
                          {item.icon}
                        </div>
                        <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                          {item.tag}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Community Q/A Section */}
          <section id="qa" className="relative">
            <div className="absolute -left-8 top-1/2 w-16 h-16 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full opacity-50"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Community</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Community for real-world questions
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mb-12">
                Discuss architectures, protocols, lab constraints, teaching tricks
                and industry transfer.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: "Technical & teaching Q/A",
                    desc: "Ask about methods, pipelines, experiments or how to explain a hard idea simply.",
                    icon: <MessageCircle className="w-5 h-5" />,
                    color: "bg-blue-100 text-blue-600"
                  },
                  {
                    title: "Peer mentoring",
                    desc: "Get quick feedback, code sessions and career advice from people in your field.",
                    icon: <Users className="w-5 h-5" />,
                    color: "bg-purple-100 text-purple-600"
                  },
                  {
                    title: "Clear explanations",
                    desc: "Work with others (and AI) to turn dense work into accessible content.",
                    icon: <BookOpen className="w-5 h-5" />,
                    color: "bg-emerald-100 text-emerald-600"
                  }
                ].map((item, index) => (
                  <div key={index} className="group">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color}`}>
                          {item.icon}
                        </div>
                        <span className="text-xs font-semibold text-gray-600">Discussion</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Marketplace Section - Research to Industry Partnership */}
          <section id="marketplace" className="relative">
            <div className="absolute -left-8 top-0 w-16 h-16 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full opacity-50"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Marketplace</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Partner with industry at every stage
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mb-12">
                Connect your research with companies seeking innovation. Whether you have an idea, a prototype, 
                a lab experiment, or a solution ready for industrial application, find partners to take it further.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    stage: "Idea",
                    title: "Early-stage concepts",
                    desc: "Share your novel ideas and get feedback, collaboration, or funding from industry partners interested in emerging technologies.",
                    icon: <Lightbulb className="w-5 h-5" />,
                    color: "bg-purple-100 text-purple-600"
                  },
                  {
                    stage: "Prototype",
                    title: "Functional prototypes",
                    desc: "Showcase working prototypes that demonstrate feasibility, attracting partners for development and testing.",
                    icon: <Cpu className="w-5 h-5" />,
                    color: "bg-blue-100 text-blue-600"
                  },
                  {
                    stage: "Lab Experiment",
                    title: "Validated experiments",
                    desc: "Present experimental results and methodologies for industry to validate and scale into real-world applications.",
                    icon: <FlaskConical className="w-5 h-5" />,
                    color: "bg-green-100 text-green-600"
                  },
                  {
                    stage: "Ready for Application",
                    title: "Market-ready solutions",
                    desc: "List fully developed solutions ready for industrial deployment, licensing, or co-development.",
                    icon: <Rocket className="w-5 h-5" />,
                    color: "bg-orange-100 text-orange-600"
                  }
                ].map((item, index) => (
                  <div key={index} className="group">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-lg transition-all duration-300 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.color}`}>
                          {item.icon}
                        </div>
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                          {item.stage}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional marketplace description */}
              <div className="mt-12 bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 rounded-2xl p-8">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                        Connect, collaborate, commercialize
                      </span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Our marketplace connects academic researchers with industry partners looking for innovation. 
                      List your work at any stage — from initial concept to market-ready solution — and find the 
                      right partner to help you scale. Get funding, development support, and licensing opportunities 
                      while maintaining control of your intellectual property.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* AI Copilot Section */}
          <section id="ia" className="relative">
            <div className="absolute -right-8 top-0 w-16 h-16 bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full opacity-50"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600 uppercase tracking-wider">
                  AI Copilot
                </span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                AI copilot for autonomous learning
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mb-12">
                AI that organizes, explains and sequences content — always grounded
                in your own work and goals.
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 rounded-2xl p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                        Axion Copilot
                      </span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Summarizes papers, turns PDFs into lesson plans or short videos,
                      builds learning paths, suggests quizzes and projects, and updates
                      your profile as you grow. A tutor that learns with you, not instead of you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to transform your academic work?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of researchers and educators building their digital presence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/register"
                className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-blue-600 bg-white rounded-lg hover:bg-gray-50 transition-colors"
              >
                Start Free Trial
              </a>
              <a
                href="/demo"
                className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
              >
                Request Demo
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}